import React, { useState, useEffect, useCallback } from 'react';
import { 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Box,
  CircularProgress,
  Alert,
  Paper,
  Typography
} from '@mui/material';
import { format } from 'date-fns'; // Add date-fns format import
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ScatterChart, Scatter, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { fetchErrorMetrics } from '../../../services/errorMetricsService';


const errorMetricTypes = [
    { value: 'ErrorMsg', label: 'Battery Error' },
    { value: 'ErrorMsg2', label: 'Battery Error 2' },
    { value: 'R_Err1', label: 'Rectifier Error 1' },
    { value: 'R_Err2', label: 'Rectifier Error 2' },
    { value: 'Err', label: 'Inverter Error' }
];

const timeFilters = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' }
];

const menuItemStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  '& .MuiMenuItem-root': {
    borderBottom: '1px solid #eee',
    py: 1,  // Reduced vertical padding
    px: 0.5,  // Minimal horizontal padding
    '&:last-child': {
      borderBottom: 'none'
    },
    '&:hover': {
      backgroundColor: '#f5f5f5'
    }
  },
  '& .MuiList-root': {
    padding: '2px 0',  // Reduced padding for the menu list container
  },
  '& .MuiPaper-root': {
    minWidth: 'auto'  // Allow paper to shrink to content
  }
};

const timeRangeMenuItemStyle = {
  my: 0.5,  // Add vertical margin between items
  minHeight: '32px',  // Reduced height
  display: 'flex',
  alignItems: 'center',
  px: 0.5,  // Reduced horizontal padding
  fontSize: '1rem'  // Default font size
};

// Add color function before the component
const getColorForCategory = (category) => {
    const colors = {
        ErrorMsg: '#FF6384',
        ErrorMsg2: '#36A2EB',
        R_Err1: '#FFCE56',
        R_Err2: '#4BC0C0',
        Err: '#9966FF'
    };
    return colors[category] || '#999999';
};

const getErrorMessageKey = (selectedError) => {
    const messageKeys = {
        'ErrorMsg': 'ErrorMsgMessage',
        'ErrorMsg2': 'ErrorMsg2Message',
        'R_Err1': 'R_Err1Message',
        'R_Err2': 'R_Err2Message',
        'Err': 'ErrMessage'
    };
    return messageKeys[selectedError] || 'ErrorMsgMessage';
};

// Define colors for different error messages
const errorMessageColors = {
    'CELL UNDER VOLTAGE': '#FF0000',           // Red
    'CELL OVER VOLTAGE': '#FF4500',            // OrangeRed
    'OVER TEMPERATURE': '#FF8C00',             // DarkOrange
    'CAN COMMUNICATION ERROR': '#FFD700',       // Gold
    'MAIN CONTACTOR FAULTY': '#32CD32',        // LimeGreen
    'SOC ERROR': '#00FF00',                    // Lime
    'DC BUS OVER CURRENT': '#4169E1',          // RoyalBlue
    'CELLS BALANCING ERROR': '#0000FF',        // Blue
    'SLAVE DEAD ERROR': '#8B008B',             // DarkMagenta
    'FAULTY CHARGING ERROR': '#800080',        // Purple
    'FAN ERROR': '#FF1493',                    // DeepPink
    'OUTPUT OVER VOLTAGE': '#FF69B4',          // HotPink
    'COMMAND NOT SEND': '#CD5C5C',             // IndianRed
    'POWER LIMIT FLAG': '#B22222',             // FireBrick
    'PHASE LOST': '#8B4513',                   // SaddleBrown
    'PHASE UNBALANCE': '#A0522D',             // Sienna
    'INPUT UNDER VOLTAGE': '#6B8E23',          // OliveDrab
    'INPUT OVER VOLTAGE': '#556B2F',           // DarkOliveGreen
    'PFC OFF ERROR': '#483D8B',               // DarkSlateBlue
    'OVER LOAD': '#4B0082',                   // Indigo
    'SHORT CIRCUIT': '#8A2BE2',               // BlueViolet
    'OTW ERROR': '#9400D3',                   // DarkViolet
    'SOW ERROR': '#9932CC',                   // DarkOrchid
    'No Error': '#808080'                     // Gray
};

const errorMessageGroups = {
    ErrorMsg: {
        'CELL UNDER VOLTAGE': '#FF0000',
        'CELL OVER VOLTAGE': '#FF4500',
        'OVER TEMPERATURE': '#FF8C00',
        'CAN COMMUNICATION ERROR': '#FFD700',
        'MAIN CONTACTOR FAULTY': '#32CD32',
        'SOC ERROR': '#00FF00',
        'DC BUS OVER CURRENT': '#4169E1',
        'CELLS BALANCING ERROR': '#0000FF'
    },
    ErrorMsg2: {
        'SLAVE DEAD ERROR': '#8B008B',
        'FAULTY CHARGING ERROR': '#800080'
    },
    R_Err1: {
        'FAN ERROR': '#FF1493',
        'OVER TEMPERATURE': '#FF8C00',
        'OUTPUT OVER VOLTAGE': '#FF69B4',
        'COMMAND NOT SEND': '#CD5C5C',
        'POWER LIMIT FLAG': '#B22222',
        'PHASE LOST': '#8B4513',
        'PHASE UNBALANCE': '#A0522D',
        'INPUT UNDER VOLTAGE': '#6B8E23'
    },
    R_Err2: {
        'INPUT OVER VOLTAGE': '#556B2F',
        'PFC OFF ERROR': '#483D8B'
    },
    Err: {
        'OVER TEMPERATURE': '#FF8C00',
        'OVER LOAD': '#4B0082',
        'SHORT CIRCUIT': '#8A2BE2',
        'OTW ERROR': '#9400D3',
        'SOW ERROR': '#9932CC'
    }
};

// Add custom legend component for error messages
const CustomLegend = ({ selectedError }) => {
    const relevantMessages = errorMessageGroups[selectedError] || {};
    
    return (
        <div style={{
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            padding: '8px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            position: 'absolute',
            top: '10px',  // Adjusted positioning
            right: '20px', // Adjusted positioning
            maxHeight: '300px',
            overflowY: 'auto',
            fontSize: '12px',
            minWidth: '180px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            zIndex: 10 // Ensure legend appears above chart
        }}>
            {Object.entries(relevantMessages).map(([message, color]) => (
                <div key={message} style={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    marginBottom: '4px',
                }}>
                    <div style={{
                        width: '10px',
                        height: '10px',
                        backgroundColor: color,
                        marginRight: '6px',
                        borderRadius: '50%',
                        border: '1px solid rgba(0,0,0,0.1)'
                    }} />
                    <span style={{ flex: 1 }}>{message}</span>
                </div>
            ))}
        </div>
    );
};

const ErrorMetricsScatter = ({ emsName }) => {
    const [selectedError, setSelectedError] = useState('ErrorMsg');
    const [timeFilter, setTimeFilter] = useState('daily');
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [chartData, setChartData] = useState(null);

    const handleErrorChange = useCallback((event) => {
        setSelectedError(event.target.value);
    }, []);

    const handleTimeFilterChange = useCallback((event) => {
        setTimeFilter(event.target.value);
    }, []);

    const handleDateChange = useCallback((newDate) => {
        if (newDate && !isNaN(newDate.getTime())) {
            setSelectedDate(prevDate => {
                return newDate.getTime() !== prevDate.getTime() ? newDate : prevDate;
            });
        }
    }, []);

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <Paper elevation={3} sx={{ p: 1, bgcolor: 'background.paper' }}>
                    <Typography variant="body2">
                        Time: {format(new Date(data.x), 'yyyy-MM-dd HH:mm:ss')}
                    </Typography>
                    <Typography variant="body2">
                        Error Code: {data.y}
                    </Typography>
                    <Typography variant="body2" color="error">
                        Message: {data.errorMessage}
                    </Typography>
                </Paper>
            );
        }
        return null;
    };

    const xAxisTickFormatter = (timestamp) => {
        return format(
            new Date(timestamp), 
            timeFilter === 'daily' ? 'HH:mm' : 
            timeFilter === 'weekly' ? 'EEE' : 'MMM dd'
        );
    };

    // Update the scatter plot to use message-based colors
    const getColorForMessage = (message) => {
        return errorMessageColors[message] || '#808080';
    };

    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                const response = await fetchErrorMetrics(emsName, timeFilter, selectedDate);
                console.log('Fetched error data:', response);
                
                const dataArray = response?.data || [];
                
                const transformedData = {
                    [selectedError]: dataArray.map(item => ({
                        x: new Date(item.timestamp).getTime(),
                        y: selectedError === 'R_Err1' || selectedError === 'R_Err2' ? 
                           item.Rec?.[selectedError] : 
                           selectedError === 'Err' ?
                           item.Inv?.Err :
                           item[selectedError],
                        errorMessage: item.errorMessages[selectedError],
                        fill: getColorForMessage(item.errorMessages[selectedError])
                    }))
                };

                console.log('Transformed chart data:', transformedData);
                setChartData(transformedData);
            } catch (err) {
                console.error('Error loading data:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [emsName, timeFilter, selectedDate, selectedError]);

    return (
        <Box sx={{ 
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
        }}>
            {/* Controls container - centered with fixed width */}
            <Box sx={{ 
                width: '1100px', // Match chart width
                display: 'flex',
                gap: 2,
                mb: 2,
                p: 2,
                bgcolor: 'background.paper',
                borderRadius: 1,
                justifyContent: 'center'
            }}>
                <FormControl size="small">
                    <InputLabel>Error Type</InputLabel>
                    <Select
                        value={selectedError}
                        label="Error Type"
                        onChange={handleErrorChange}
                        MenuProps={{
                            PaperProps: {
                                sx: menuItemStyle
                            }
                        }}
                    >
                        {errorMetricTypes.map((errorType) => (
                            <MenuItem 
                                key={errorType.value} 
                                value={errorType.value}
                                sx={{
                                    minHeight: '32px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    px: 0.5,
                                    fontSize: '1rem'
                                }}
                            >
                                {errorType.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl size="small">
                    <InputLabel>Time Range</InputLabel>
                    <Select
                        value={timeFilter}
                        label="Time Range"
                        onChange={handleTimeFilterChange}
                        MenuProps={{
                            PaperProps: {
                                sx: menuItemStyle
                            },
                            anchorOrigin: {
                                vertical: 'bottom',
                                horizontal: 'left'
                            },
                            transformOrigin: {
                                vertical: 'top',
                                horizontal: 'left'
                            }
                        }}
                    >
                        {timeFilters.map((filter) => (
                            <MenuItem 
                                key={filter.value} 
                                value={filter.value}
                                sx={timeRangeMenuItemStyle}
                            >
                                {filter.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl size="small">
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                            label="Select Date"
                            value={selectedDate}
                            onChange={handleDateChange}
                            maxDate={new Date()}
                            views={['year', 'month', 'day']}
                            slotProps={{
                                textField: {
                                    size: "small",
                                    fullWidth: true,
                                    inputProps: {
                                        readOnly: true
                                    }
                                }
                            }}
                        />
                    </LocalizationProvider>
                </FormControl>
            </Box>

            {/* Chart container - aligned with controls */}
            <Box sx={{ 
                width: '1000px', // Match controls width
                mt: 2, 
                mb: 3,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'relative' // Important for legend positioning
            }}>
                {loading ? (
                    <CircularProgress />
                ) : error ? (
                    <Alert severity="error">{error}</Alert>
                ) : !chartData || chartData[selectedError].length === 0 ? (
                    <Alert severity="info">No error data available on selected date.</Alert>
                ) : (
                    <div style={{
                        width: '100%',
                        height: '400px',
                        position: 'relative'
                    }}>
                        <ResponsiveContainer>
                            <ScatterChart
                                margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="x"
                                    type="number"
                                    scale="time"
                                    domain={['auto', 'auto']}
                                    tickFormatter={xAxisTickFormatter}
                                    label={{ value: 'Time', position: 'bottom' }}
                                    ticks={chartData[selectedError]?.map(point => point.x)
                                        .filter((value, index, self) => 
                                            self.indexOf(value) === index
                                        )}
                                />
                                <YAxis
                                    dataKey="y"
                                    type="number"
                                    name="Error Code"
                                    label={{ 
                                        value: 'Error Code', 
                                        angle: -90, 
                                        position: 'insideLeft' 
                                    }}
                                />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend 
                                    content={<CustomLegend selectedError={selectedError} />}
                                    wrapperStyle={{ position: 'absolute', top: 0, right: 0 }}
                                />
                                <Scatter
                                    name={errorMetricTypes.find(t => t.value === selectedError)?.label}
                                    data={chartData[selectedError] || []}
                                />
                            </ScatterChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </Box>
        </Box>
    );
};

export default React.memo(ErrorMetricsScatter);